/**
 * CILSON 3D Kanalplaner PRO - Cutting Engine
 * Reine, DOM-freie Kernlogik: Positionsnummerierung, Zuschnittoptimierung,
 * Reststueck-Verwaltung und Gehrungsgeometrie.
 *
 * Alle Laengen werden als ganze Millimeter (Integer) verarbeitet.
 * Kein Mischen unterschiedlicher Produkte (Dimension) oder Farben in einem Rohstab.
 * Maximale Rohstablaenge: 2000mm (konfigurierbar über options.stockLengthMm).
 *
 * Wird als eigenstaendiges <script> geladen und stellt sich global unter
 * window.CilsonCuttingEngine zur Verfuegung (auch aus der Browser-Konsole testbar).
 */
(function (global) {
  'use strict';

  var DEFAULT_STOCK_LENGTH_MM = 2000;
  var DEFAULT_SAW_KERF_MM = 3;
  var DEFAULT_MIN_LEFTOVER_MM = 150;

  function assertInt(value, name) {
    if (!Number.isInteger(value)) {
      throw new Error('CilsonCuttingEngine: ' + name + ' muss eine ganze Zahl (mm) sein, erhalten: ' + value);
    }
  }

  /**
   * Teilt Segmente, die laenger als die Rohstablaenge sind, in mehrere Teilstuecke.
   * Jedes Teilstueck erhaelt eine eigene subIndex-Kennung, behaelt aber die
   * urspruengliche Segment-UUID als parentUuid fuer Rueckverfolgbarkeit.
   */
  function splitOversizedSegments(segments, stockLengthMm) {
    stockLengthMm = stockLengthMm || DEFAULT_STOCK_LENGTH_MM;
    var result = [];
    segments.forEach(function (seg) {
      assertInt(seg.finishedLengthMm, 'finishedLengthMm');
      if (seg.finishedLengthMm <= stockLengthMm) {
        result.push(Object.assign({}, seg, { parentUuid: seg.uuid, splitIndex: 0, splitTotal: 1 }));
        return;
      }
      var remaining = seg.finishedLengthMm;
      var parts = [];
      while (remaining > 0) {
        var pieceLen = Math.min(stockLengthMm, remaining);
        parts.push(pieceLen);
        remaining -= pieceLen;
      }
      parts.forEach(function (len, idx) {
        result.push(Object.assign({}, seg, {
          uuid: seg.uuid + '-split' + (idx + 1),
          parentUuid: seg.uuid,
          finishedLengthMm: len,
          splitIndex: idx,
          splitTotal: parts.length,
          autoSplit: true
        }));
      });
    });
    return result;
  }

  /**
   * Vergibt fortlaufende Positionsnummern (ab 1) in Reihenfolge der uebergebenen
   * Liste (Aufrufer ist verantwortlich fuer die Montagereihenfolge: Wand fuer Wand,
   * Lauf fuer Lauf, Raum fuer Raum).
   */
  function assignPositionNumbers(orderedSegments) {
    var next = 1;
    return orderedSegments.map(function (seg) {
      return Object.assign({}, seg, { positionNumber: next++ });
    });
  }

  function groupKey(seg) {
    return [seg.productHandle, seg.colorCode].join('||');
  }

  /**
   * Zuschnittoptimierung: First-Fit-Decreasing Bin-Packing je Produkt+Farbe-Gruppe
   * auf Rohstaebe der Laenge stockLengthMm, unter Beruecksichtigung des Saegeschnitts.
   * Es werden NIE unterschiedliche Produkte/Farben in denselben Rohstab gemischt.
   */
  function optimizeCutting(segments, options) {
    options = options || {};
    var stockLengthMm = options.stockLengthMm || DEFAULT_STOCK_LENGTH_MM;
    var sawKerfMm = options.sawKerfMm != null ? options.sawKerfMm : DEFAULT_SAW_KERF_MM;
    var minLeftoverMm = options.minLeftoverMm != null ? options.minLeftoverMm : DEFAULT_MIN_LEFTOVER_MM;

    assertInt(stockLengthMm, 'stockLengthMm');
    assertInt(sawKerfMm, 'sawKerfMm');

    var groups = {};
    segments.forEach(function (seg) {
      assertInt(seg.finishedLengthMm, 'finishedLengthMm');
      if (seg.finishedLengthMm > stockLengthMm) {
        throw new Error('CilsonCuttingEngine: Segment ' + seg.uuid + ' ist ' + seg.finishedLengthMm +
          'mm lang und ueberschreitet die maximale Rohstablaenge von ' + stockLengthMm +
          'mm. Bitte zuerst splitOversizedSegments() aufrufen.');
      }
      var key = groupKey(seg);
      if (!groups[key]) groups[key] = { productHandle: seg.productHandle, colorCode: seg.colorCode, items: [] };
      groups[key].items.push(seg);
    });

    var allRods = [];
    var allLeftovers = [];
    var rodCounter = 1;

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];
      var items = group.items.slice().sort(function (a, b) { return b.finishedLengthMm - a.finishedLengthMm; });
      var rods = [];

      items.forEach(function (item) {
        var placed = false;
        for (var i = 0; i < rods.length; i++) {
          var rod = rods[i];
          var kerfForThisPiece = rod.pieces.length > 0 ? sawKerfMm : 0;
          var neededLen = item.finishedLengthMm + kerfForThisPiece;
          if (rod.usedMm + neededLen <= stockLengthMm) {
            var startMm = rod.usedMm + kerfForThisPiece;
            rod.pieces.push({
              positionNumber: item.positionNumber,
              uuid: item.uuid,
              startMm: startMm,
              endMm: startMm + item.finishedLengthMm
            });
            rod.usedMm = startMm + item.finishedLengthMm;
            placed = true;
            break;
          }
        }
        if (!placed) {
          rods.push({
            rodNumber: null,
            productHandle: group.productHandle,
            colorCode: group.colorCode,
            pieces: [{ positionNumber: item.positionNumber, uuid: item.uuid, startMm: 0, endMm: item.finishedLengthMm }],
            usedMm: item.finishedLengthMm
          });
        }
      });

      rods.forEach(function (rod) {
        rod.rodNumber = rodCounter++;
        var leftoverMm = stockLengthMm - rod.usedMm;
        rod.leftoverMm = leftoverMm;
        allRods.push(rod);
        if (leftoverMm > 0) {
          allLeftovers.push({
            id: 'R' + rod.rodNumber + '-' + group.colorCode,
            rodNumber: rod.rodNumber,
            productHandle: group.productHandle,
            colorCode: group.colorCode,
            lengthMm: leftoverMm,
            deliverable: leftoverMm >= minLeftoverMm,
            decision: leftoverMm >= minLeftoverMm ? 'deliver' : 'recycle'
          });
        }
      });
    });

    return {
      rods: allRods,
      leftovers: allLeftovers,
      totalRods: allRods.length,
      stockLengthMm: stockLengthMm,
      sawKerfMm: sawKerfMm
    };
  }

  /**
   * Berechnet die tatsaechliche Gehrungsgeometrie fuer eine Ecke anhand des
   * gemessenen Wandwinkels (in Grad, 0-360). Es wird NICHT pauschal von 45/45
   * bei 90 Grad ausgegangen, sondern generisch aus dem Innenwinkel berechnet:
   * miterAngleDeg = wallAngleDeg / 2 (Standard-Gehrungsformel fuer symmetrische
   * Eckverbindungen zweier gleich breiter Profile).
   */
  function calculateMiterGeometry(wallAngleDeg, direction) {
    if (typeof wallAngleDeg !== 'number' || wallAngleDeg <= 0 || wallAngleDeg >= 360) {
      throw new Error('CilsonCuttingEngine: wallAngleDeg muss zwischen 0 und 360 Grad liegen.');
    }
    var miterAngleDeg = wallAngleDeg / 2;
    return {
      wallAngleDeg: wallAngleDeg,
      miterAngleDeg: Math.round(miterAngleDeg * 100) / 100,
      direction: direction || 'right',
      isGeometricallyValid: miterAngleDeg > 0 && miterAngleDeg < 90
    };
  }

  /**
   * Fuehrt eine Reihe interner Selbsttests aus (angelehnt an Sec. 18 Testfaelle,
   * soweit sie die Kernlogik betreffen). Gibt ein Array mit {name, pass, details} zurueck.
   * Ersetzt KEINEN echten Testrunner (Vitest/Playwright steht nicht zur Verfuegung),
   * dient aber als nachvollziehbarer, reproduzierbarer Nachweis in der Konsole.
   */
  function runSelfTests() {
    var results = [];
    function check(name, fn) {
      try {
        var detail = fn();
        results.push({ name: name, pass: true, details: detail || 'ok' });
      } catch (e) {
        results.push({ name: name, pass: false, details: e.message });
      }
    }

    check('TC1: 1000+600 aus einem 2000mm Stab', function () {
      var segs = [
        { uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 1000, positionNumber: 1 },
        { uuid: 'b', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 600, positionNumber: 2 }
      ];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (res.totalRods !== 1) throw new Error('erwartet 1 Stab, erhalten ' + res.totalRods);
      if (res.rods[0].leftoverMm !== 397) throw new Error('erwartet 397mm Rest, erhalten ' + res.rods[0].leftoverMm);
      return '1 Stab, Rest ' + res.rods[0].leftoverMm + 'mm';
    });

    check('TC2: exakt 2000mm', function () {
      var segs = [{ uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 2000, positionNumber: 1 }];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (res.totalRods !== 1 || res.rods[0].leftoverMm !== 0) throw new Error('erwartet 1 Stab ohne Rest');
      return '1 Stab, kein Rest';
    });

    check('TC3: 2001mm muss vor Optimierung gesplittet werden', function () {
      var segs = [{ uuid: 'a', uuidBase: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 2001, positionNumber: 1 }];
      var split = splitOversizedSegments(segs, 2000);
      if (split.length !== 2) throw new Error('erwartet 2 Teilstuecke, erhalten ' + split.length);
      if (split[0].finishedLengthMm !== 2000 || split[1].finishedLengthMm !== 1) {
        throw new Error('erwartete Aufteilung 2000+1, erhalten ' + split.map(function (s) { return s.finishedLengthMm; }).join('+'));
      }
      return 'Split in 2000mm + 1mm';
    });

    check('TC4: 4600mm -> 2000+2000+600', function () {
      var segs = [{ uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 4600, positionNumber: 1 }];
      var split = splitOversizedSegments(segs, 2000);
      var lens = split.map(function (s) { return s.finishedLengthMm; });
      if (lens.join(',') !== '2000,2000,600') throw new Error('erhalten ' + lens.join(','));
      return lens.join('+');
    });

    check('TC5: 900+700+350 optimale Packung', function () {
      var segs = [
        { uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 900, positionNumber: 1 },
        { uuid: 'b', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 700, positionNumber: 2 },
        { uuid: 'c', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 350, positionNumber: 3 }
      ];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (res.totalRods !== 1) throw new Error('erwartet 1 Stab (900+700+350+2x3 Kerf=1956mm<=2000), erhalten ' + res.totalRods);
      return '1 Stab, Rest ' + res.rods[0].leftoverMm + 'mm';
    });

    check('TC6: unterschiedliche Farben duerfen nicht gemischt werden', function () {
      var segs = [
        { uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 500, positionNumber: 1 },
        { uuid: 'b', productHandle: 'p1', colorCode: 'schwarz', finishedLengthMm: 500, positionNumber: 2 }
      ];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (res.totalRods !== 2) throw new Error('erwartet 2 separate Staebe, erhalten ' + res.totalRods);
      return '2 separate Staebe (Farben getrennt)';
    });

    check('TC7: unterschiedliche Dimensionen duerfen nicht gemischt werden', function () {
      var segs = [
        { uuid: 'a', productHandle: 'kanal-100x60', colorCode: 'weiss', finishedLengthMm: 500, positionNumber: 1 },
        { uuid: 'b', productHandle: 'kanal-120x60', colorCode: 'weiss', finishedLengthMm: 500, positionNumber: 2 }
      ];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (res.totalRods !== 2) throw new Error('erwartet 2 separate Staebe, erhalten ' + res.totalRods);
      return '2 separate Staebe (Dimensionen getrennt)';
    });

    check('TC8: 90 Grad Ecke ergibt 45 Grad Gehrung je Teil', function () {
      var geo = calculateMiterGeometry(90, 'left');
      if (geo.miterAngleDeg !== 45) throw new Error('erwartet 45 Grad, erhalten ' + geo.miterAngleDeg);
      return 'Gehrungswinkel ' + geo.miterAngleDeg + ' Grad';
    });

    check('TC8b: 135 Grad Ecke ergibt KEINE pauschalen 45 Grad', function () {
      var geo = calculateMiterGeometry(135, 'left');
      if (geo.miterAngleDeg === 45) throw new Error('Gehrung darf bei 135 Grad nicht pauschal 45 Grad sein');
      if (geo.miterAngleDeg !== 67.5) throw new Error('erwartet 67.5 Grad, erhalten ' + geo.miterAngleDeg);
      return 'Gehrungswinkel ' + geo.miterAngleDeg + ' Grad (korrekt aus 135 Grad-Ecke berechnet)';
    });

    check('TC9: Reststueck ab Mindestlaenge lieferbar', function () {
      var segs = [{ uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 1500, positionNumber: 1 }];
      var res = optimizeCutting(segs, { sawKerfMm: 3, minLeftoverMm: 150 });
      if (res.leftovers.length !== 1) throw new Error('erwartet 1 Reststueck');
      if (res.leftovers[0].decision !== 'deliver') throw new Error('erwartet deliver bei 500mm Rest');
      return 'Reststueck ' + res.leftovers[0].lengthMm + 'mm -> ' + res.leftovers[0].decision;
    });

    check('TC9b: Reststueck unter Mindestlaenge wird recycelt', function () {
      var segs = [{ uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 1900, positionNumber: 1 }];
      var res = optimizeCutting(segs, { sawKerfMm: 3, minLeftoverMm: 150 });
      if (res.leftovers[0].decision !== 'recycle') throw new Error('erwartet recycle bei 100mm Rest');
      return 'Reststueck ' + res.leftovers[0].lengthMm + 'mm -> ' + res.leftovers[0].decision;
    });

    check('TC11: Positionsnummern beginnen bei 1', function () {
      var segs = [{ uuid: 'a' }, { uuid: 'b' }, { uuid: 'c' }];
      var numbered = assignPositionNumbers(segs);
      if (numbered[0].positionNumber !== 1) throw new Error('erste Position muss 1 sein');
      return numbered.map(function (s) { return s.positionNumber; }).join(',');
    });

    check('TC17: alle Laengen sind Integer (keine Rundungsfehler)', function () {
      var segs = [{ uuid: 'a', productHandle: 'p1', colorCode: 'weiss', finishedLengthMm: 333, positionNumber: 1 }];
      var res = optimizeCutting(segs, { sawKerfMm: 3 });
      if (!Number.isInteger(res.rods[0].leftoverMm)) throw new Error('leftoverMm ist kein Integer');
      return 'leftoverMm=' + res.rods[0].leftoverMm + ' (Integer)';
    });

    return results;
  }

  global.CilsonCuttingEngine = {
    DEFAULT_STOCK_LENGTH_MM: DEFAULT_STOCK_LENGTH_MM,
    DEFAULT_SAW_KERF_MM: DEFAULT_SAW_KERF_MM,
    DEFAULT_MIN_LEFTOVER_MM: DEFAULT_MIN_LEFTOVER_MM,
    splitOversizedSegments: splitOversizedSegments,
    assignPositionNumbers: assignPositionNumbers,
    optimizeCutting: optimizeCutting,
    calculateMiterGeometry: calculateMiterGeometry,
    runSelfTests: runSelfTests
  };
})(window);
